import { eqProps, equals, keys } from 'ramda';
import type { Location, NodeEntry, Path, Span } from 'slate';
import { Editor, Node } from 'slate';

import type {
  ListDefinition,
  ListDefinitions,
  ListItemElement,
  ListItemProps,
  ListLevel,
  ListLevelDefinition,
  ListMarker,
  ParagraphElement,
  ParagraphProps,
} from '../internal';
import {
  AlineTransforms,
  isListItem,
  isListLevel,
  isOrderedListMarkerStyle,
  isParagraphElement,
  ListLevelAlignment,
  Queries,
} from '../internal';

/**
 * Advance a list's per-level counter array for one list item — the single
 * source of truth for list numbering, shared by the editor's on-screen markers
 * (`getListItemPath`, `listMarker`) and by Cast/extraction numbering, so all
 * three count identically. Given the running counter for a `listId` and the
 * item's `listLevel`: the item's level increments, deeper levels are dropped,
 * and any not-yet-seen level initializes from its definition's `start`.
 */
export function advanceListCounter(
  prevCounter: Path,
  listLevel: number,
  listDefinition: ListDefinition
): Path {
  return Array.from({ length: listLevel + 1 }, (_, n) => {
    if (typeof prevCounter[n] !== 'number') {
      if (!isListLevel(n)) return 0;
      const listLevelDefinition = listDefinition[n];
      if (!listLevelDefinition) return 0;
      return typeof listLevelDefinition.start === 'number' ? listLevelDefinition.start - 1 : 0;
    }
    return n === listLevel ? prevCounter[n] + 1 : prevCounter[n];
  });
}

export default {
  /**
   * Returns the alignment for a given list level.
   */
  activeListLevelAlignment(levelDefinition: ListLevelDefinition): ListLevelAlignment {
    return levelDefinition.alignment ?? ListLevelAlignment.Start;
  },

  /**
   * Given a location, determines what
   */
  activeListMarkerAtLocation(
    editor: Editor,
    options: {
      at?: Location;
    } = {}
  ): ListMarker | null {
    const { at = editor.selection } = options;
    if (!at) return null;

    const paragraphEntries = Queries.paragraphsAtLocation(editor, { at });
    const firstParagraphEntry = paragraphEntries.next().value;
    if (!firstParagraphEntry) return null;

    return Queries.listMarker(editor, firstParagraphEntry);
  },

  /**
   * Calculate the formats for the list marker for a given paragraph,
   * if the paragraph is a list item.
   */
  activeListMarkerDetails(
    listDefinitions: ListDefinitions,
    paragraph: ParagraphProps
  ): {
    activeListProps: ListItemProps['listProps'];
    levelDefinition: ListLevelDefinition;
    listDefinition: ListDefinition;
  } | null {
    const activeListProps = Queries.renderedParagraphListProps(paragraph);
    if (!activeListProps) return null;

    const listDefinition = listDefinitions[activeListProps.listId];
    if (!listDefinition) return null;

    const levelDefinition = listDefinition[activeListProps.listLevel];
    if (!levelDefinition) return null;

    return {
      activeListProps,
      levelDefinition,
      listDefinition,
    };
  },
  /**
   * Get the *consistent* list definition for a given location.
   */
  consistentListDefinition(editor: Editor, options: { at?: Location } = {}): ListDefinition | null {
    const { at = editor.selection } = options;
    if (!at) return null;

    const paragraphEntries = Queries.paragraphsAtLocation(editor, { at });
    const firstEntry = paragraphEntries.next().value;
    if (!firstEntry) return null;

    const [firstParagraph] = firstEntry;
    const firstParaListDetails = Queries.getListItemDetails(editor, firstParagraph);
    if (!firstParaListDetails) return null;

    const { listDefinition } = firstParaListDetails;

    for (const [paragraph] of paragraphEntries) {
      const listDetails = Queries.getListItemDetails(editor, paragraph);
      if (!listDetails || !equals(listDetails.listDefinition, listDefinition)) return null;
    }

    return listDefinition;
  },

  /**
   * Compute the listItemPath (numbering counter array) for a list paragraph.
   * Scans all list items with the same listId from document start to paragraphPath,
   * incrementing counters at each level.
   */
  getListItemPath(editor: Editor, paragraphPath: Path, listId: string): Path | null {
    const { listDefinitions } = Queries.getNumbering(editor);
    const listDefinition = listId in listDefinitions ? listDefinitions[listId] : null;
    if (!listDefinition) return null;

    const span: Span = [[], paragraphPath];
    const listItemEntries = [
      ...Editor.nodes<ListItemElement>(editor, {
        at: span,
        match: n => {
          if (!isParagraphElement(n)) return false;
          const nodeListProps = Queries.renderedParagraphListProps(n);
          return nodeListProps?.listId === listId;
        },
        voids: true,
      }),
    ];

    return listItemEntries.reduce<Path>((acc, [listItem]) => {
      const listItemListProps = Queries.renderedParagraphListProps(listItem);
      if (!listItemListProps) return acc;

      return advanceListCounter(acc, listItemListProps.listLevel, listDefinition);
    }, []);
  },

  /**
   * Get every ordered (numbered/lettered) list item in the document, in
   * document order. Bullet and symbol list items are excluded since they have
   * no number to cross-reference.
   */
  getOrderedListItems(editor: Editor): NodeEntry<ParagraphElement>[] {
    return [
      ...Editor.nodes<ParagraphElement>(editor, {
        at: [],
        match: node => {
          if (!isListItem(node)) return false;
          const details = Queries.getListItemDetails(editor, node);
          if (!details) return false;

          const { activeListProps, listDefinition } = details;
          const levelDefinition = listDefinition[activeListProps.listLevel];
          if (!levelDefinition) return false;

          return isOrderedListMarkerStyle(levelDefinition.style.name);
        },
        voids: true,
      }),
    ];
  },

  /**
   * Determine if the list marker for a paragraph is selected.
   */
  isListItemSelected(editor: Editor, listItemPath: Path): boolean {
    if (!editor.selection?.listMarker) return false;

    const listItem = Node.get(editor, listItemPath);
    if (!isParagraphElement(listItem)) return false;

    const activeListProps = Queries.renderedParagraphListProps(listItem);
    const focusParagraphEntry = Queries.paragraphAtPoint(editor);
    if (!activeListProps || !focusParagraphEntry) return false;

    const [focusParagraph] = focusParagraphEntry;
    const focusParaListProps = Queries.renderedParagraphListProps(focusParagraph);
    if (!focusParaListProps) return false;

    return (
      focusParaListProps.listLevel === activeListProps.listLevel &&
      focusParaListProps.listId === activeListProps.listId
    );
  },

  /**
   * Get the correct ListMarker for a paragraph, if it's a list item.
   */
  listMarker(editor: Editor, paraEntry: NodeEntry<ParagraphElement>): ListMarker | null {
    const [paragraph, paragraphPath] = paraEntry;
    const activeListProps = Queries.renderedParagraphListProps(paragraph);
    if (!isListItem(paragraph) || !activeListProps) return null;

    const span: Span = [[], paragraphPath];
    const listItemEntriesBeforeAndAtLocation = [
      ...Editor.nodes<ListItemElement>(editor, {
        at: span,
        match: n => {
          if (!isParagraphElement(n)) return false;

          const nodeListProps = Queries.renderedParagraphListProps(n);
          return nodeListProps?.listId === activeListProps.listId;
        },
        voids: true,
      }),
    ];
    const { listDefinitions } = Queries.getNumbering(editor);
    const listDefinition =
      activeListProps.listId in listDefinitions ? listDefinitions[activeListProps.listId] : null;
    const listItemPath = listDefinition
      ? listItemEntriesBeforeAndAtLocation.reduce<Path>((acc, [listItem]) => {
          const listItemListProps = Queries.renderedParagraphListProps(listItem);
          if (!listItemListProps) return acc;

          return advanceListCounter(acc, listItemListProps.listLevel, listDefinition);
        }, [])
      : [];

    return AlineTransforms.buildListMarker(editor, {
      listDefinitions,
      listItem: paragraph,
      listItemPath,
    });
  },

  /**
   * Returns whether the list style of 2 list definitions are equal.
   */
  listStylesEqual: (listDefinitionA: ListDefinition, listDefinitionB: ListDefinition): boolean => {
    const styleProp: keyof ListDefinition[ListLevel] = 'style';

    return keys(listDefinitionA).every(level =>
      eqProps(styleProp, listDefinitionA[level], listDefinitionB[level])
    );
  },
};
